import { Rectangle, Texture } from 'pixi.js';
import { gameLogger } from '../../../utils/GameLogger';

/**
 * One baked texture source for the status pip row (plan 15 §2.4): a pip
 * circle (white fill, dark rim, so a tint colours the fill and keeps the rim)
 * and white `+`/digit glyphs with a black stroke for the `+N` overflow marker.
 * Every frame shares one source, so the row batches as one texture and its
 * sprites never need a texture swap: each glyph slot owns its frame for life.
 *
 * Built lazily on first use, once per page. Without a 2D canvas (jsdom), the
 * frames fall back to the shared white texture at the same sizes.
 */
export interface AtlasFrame {
  texture: Texture;
  /** Size in world px at scale 1. */
  width: number;
  height: number;
}

export interface StatusPipAtlas {
  circle: AtlasFrame;
  /** Keys `+` and `0`…`9`. */
  glyphs: Record<string, AtlasFrame>;
}

/** Pixels per world px in the baked canvas (crisp at 2× zoom and HiDPI). */
const RES = 4;
const CIRCLE_PX = 8;
const GLYPHS = ['+', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

let atlas: StatusPipAtlas | null = null;
let atlasKey = '';

export function getStatusPipAtlas(fontFamily: string, fontSize: number): StatusPipAtlas {
  const key = `${fontFamily}|${fontSize}`;
  if (atlas && atlasKey === key) return atlas;
  try {
    atlas = buildAtlas(fontFamily, fontSize);
  } catch (err) {
    // A canvas or texture failure must not take the pips (or the frame) down.
    gameLogger.warn('renderer', '[statusPipAtlas] bake failed; pips fall back to plain squares', err);
    atlas = fallbackAtlas(fontSize);
  }
  atlasKey = key;
  return atlas;
}

/** Test hook: forget the cached atlas. */
export function __resetStatusPipAtlasForTesting(): void {
  atlas = null;
  atlasKey = '';
}

/** The shared white texture at the same sizes (no 2D canvas: jsdom, a failed bake). */
function fallbackAtlas(fontSize: number): StatusPipAtlas {
  const frame = (w: number, h: number): AtlasFrame => ({ texture: Texture.WHITE, width: w, height: h });
  const glyphs: Record<string, AtlasFrame> = {};
  for (const g of GLYPHS) glyphs[g] = frame(Math.ceil(fontSize * 0.62), Math.ceil(fontSize * 1.4));
  return { circle: frame(CIRCLE_PX, CIRCLE_PX), glyphs };
}

function buildAtlas(fontFamily: string, fontSize: number): StatusPipAtlas {
  const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  let ctx: CanvasRenderingContext2D | null = null;
  try { ctx = canvas?.getContext('2d') ?? null; } catch { ctx = null; }
  const font = `bold ${fontSize * RES}px ${fontFamily}`;
  const stroke = 2 * RES;
  const cellH = Math.ceil(fontSize * 1.4) * RES;
  const widths = GLYPHS.map((g) => {
    if (!ctx) return Math.ceil(fontSize * 0.62) * RES;
    ctx.font = font;
    return Math.ceil(ctx.measureText(g).width + stroke + 2);
  });
  const circlePx = CIRCLE_PX * RES;
  const pad = 2;
  const totalW = circlePx + pad + widths.reduce((a, w) => a + w + pad, 0);
  const totalH = Math.max(circlePx, cellH);

  if (!ctx || !canvas) return fallbackAtlas(fontSize);

  canvas.width = totalW;
  canvas.height = totalH;
  ctx.clearRect(0, 0, totalW, totalH);
  // Pip: white fill, 1 world-px dark rim (the old strokeCircle at 0.9 alpha).
  const r = circlePx / 2;
  ctx.beginPath();
  ctx.arc(r, r, r - RES / 2, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.lineWidth = RES;
  ctx.strokeStyle = 'rgba(0,0,0,0.9)';
  ctx.stroke();

  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = stroke;
  ctx.strokeStyle = '#000000';
  ctx.fillStyle = '#ffffff';
  const rects: Rectangle[] = [];
  let x = circlePx + pad;
  GLYPHS.forEach((g, i) => {
    const cx = x + widths[i] / 2;
    ctx!.strokeText(g, cx, cellH / 2);
    ctx!.fillText(g, cx, cellH / 2);
    rects.push(new Rectangle(x, 0, widths[i], cellH));
    x += widths[i] + pad;
  });

  const source = Texture.from(canvas).source;
  const frame = (rect: Rectangle): AtlasFrame => ({
    texture: new Texture({ source, frame: rect }),
    width: rect.width / RES,
    height: rect.height / RES,
  });
  const glyphs: Record<string, AtlasFrame> = {};
  GLYPHS.forEach((g, i) => { glyphs[g] = frame(rects[i]); });
  return { circle: frame(new Rectangle(0, 0, circlePx, circlePx)), glyphs };
}
